<template>
  <div v-if="overridePretty" class="devBanner" role="status">
    <span class="devBannerMsg">
      <span class="devDot" aria-hidden="true"></span>
      <strong>Development mode</strong> — your calls (dials &amp; voicemails) route to
      {{ overridePretty }}, not real prospects.
    </span>
    <button type="button" class="devGoLive" :disabled="saving" @click="goLive">
      {{ saving ? 'Going live…' : 'Go Live' }}
    </button>
  </div>
</template>

<script setup lang="ts">
// Dialer-page banner shown when this rep has a dev override set (development mode):
// all their calls route to the override number instead of real prospects. "Go Live"
// clears the override (putTelephonyMe → refresh), which also dismisses this banner.
import { computed, ref } from 'vue'
import { useTelephony } from '../../composables/useTelephony'
import { putTelephonyMe } from '../../../services/dialerApi'
import { formatPhone } from '../../utils/phone'

const telephony = useTelephony()
const saving = ref(false)

const override = computed(() => telephony.projection.value?.me.dev_override_number || null)
const overridePretty = computed(() => (override.value ? formatPhone(override.value) || override.value : ''))

async function goLive() {
  if (saving.value) return
  saving.value = true
  try {
    // "" clears the override (→ LIVE); null would be treated as "no change" by the backend.
    await putTelephonyMe({ dev_override_number: '' })
    await telephony.refresh()
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.devBanner {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 8px var(--space-6);
  background: var(--hue-amber-soft);
  color: var(--hue-amber);
  border-bottom: 1px solid var(--hue-amber-edge);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
}
.devBannerMsg { display: inline-flex; align-items: center; gap: var(--space-2); min-width: 0; }
.devDot { width: 8px; height: 8px; border-radius: var(--radius-full); background: currentColor; flex-shrink: 0; }
.devGoLive {
  flex-shrink: 0;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  padding: 3px var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
}
.devGoLive:hover:not(:disabled) { background: var(--hue-amber); color: var(--color-text-inverse); }
.devGoLive:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
